// frontend/src/components/mascot/InteractiveSentinelBear.tsx
// TaisaCore Sprint 10: Interactive Sentinel Bear with Gaze Tracking

import React, { useRef, useCallback, useState } from 'react';
import { useSpring, animated, config } from '@react-spring/web';
import { BearSVG } from './BearSVG';
import { useMousePosition } from '../../hooks/useMousePosition';
import { useBearState } from '../../hooks/useBearState';
import './InteractiveSentinelBear.css';

interface InteractiveSentinelBearProps {
  size?: number;
  position?: 'bottom-right' | 'bottom-left' | 'inline';
  showAlways?: boolean;
  onClick?: () => void;
}

export const InteractiveSentinelBear: React.FC<InteractiveSentinelBearProps> = ({
  size = 120,
  position = 'bottom-right',
  showAlways = false,
  onClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const mousePosition = useMousePosition();
  const { bearState, currentAlert, dismissCurrentAlert } = useBearState();

  // Calculate eye offset based on mouse position relative to bear
  const calculateEyeOffset = useCallback(() => {
    if (!containerRef.current) return { x: 0, y: 0 };

    const rect = containerRef.current.getBoundingClientRect();
    const bearCenterX = rect.left + rect.width / 2;
    const bearCenterY = rect.top + rect.height / 3; // Eyes are in upper third

    const deltaX = mousePosition.x - bearCenterX;
    const deltaY = mousePosition.y - bearCenterY;

    // Calculate angle and clamp distance
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const maxOffset = 5; // Max pixels eyes can move
    const normalizedDistance = Math.min(distance / 200, 1);

    const angle = Math.atan2(deltaY, deltaX);
    const offsetX = Math.cos(angle) * normalizedDistance * maxOffset;
    const offsetY = Math.sin(angle) * normalizedDistance * maxOffset;

    return { x: offsetX, y: offsetY };
  }, [mousePosition]);

  const eyeOffset = calculateEyeOffset();

  // Smooth eye movement
  const eyeSpring = useSpring({
    transform: `translate(${eyeOffset.x}px, ${eyeOffset.y}px)`,
    config: { tension: 300, friction: 20 },
  });

  // Body animation based on state
  const bodySpring = useSpring({
    transform: bearState === 'alert'
      ? 'rotate(-5deg) scale(1.05)'
      : bearState === 'success'
      ? 'rotate(0deg) scale(1.1)'
      : 'rotate(0deg) scale(1)',
    config: config.wobbly,
  });

  // Visibility/opacity based on state and hover
  const visibilitySpring = useSpring({
    opacity: bearState === 'alert' || showAlways || isHovered ? 1 : 0.15,
    config: { duration: 300 },
  });

  // Bounce animation for alerts
  const bounceSpring = useSpring({
    y: bearState === 'alert' ? -5 : 0,
    config: { tension: 300, friction: 10 },
    loop: bearState === 'alert' ? { reverse: true } : false,
  });

  const handleClick = () => {
    if (currentAlert) {
      dismissCurrentAlert();
    }
    onClick?.();
  };

  const positionClass = position === 'inline' ? '' : `sentinel-bear--${position}`;

  return (
    <animated.div
      ref={containerRef}
      className={`sentinel-bear ${positionClass}`}
      style={{
        ...visibilitySpring,
        width: size,
        height: size * 1.2,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`TaisaCore assistant - ${bearState} state${currentAlert ? `: ${currentAlert.shortMessage}` : ''}`}
    >
      <animated.div
        className="sentinel-bear__body"
        style={{
          ...bodySpring,
          y: bounceSpring.y,
        }}
      >
        <BearSVG
          size={size}
          state={bearState}
          eyeOffset={eyeOffset}
          eyeSpring={eyeSpring}
        />
      </animated.div>

      {/* Speech bubble for alerts */}
      {currentAlert && (bearState === 'alert' || isHovered) && (
        <div
          className={`sentinel-bear__speech-bubble sentinel-bear__speech-bubble--${currentAlert.severity}`}
        >
          <span className="speech-bubble__icon">
            {currentAlert.severity === 'critical' ? '🚨' : currentAlert.severity === 'warning' ? '⚠️' : 'ℹ️'}
          </span>
          <span className="speech-bubble__text">
            {currentAlert.shortMessage}
          </span>
          <button
            className="speech-bubble__dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissCurrentAlert();
            }}
            aria-label="Dismiss alert"
          >
            ✕
          </button>
        </div>
      )}

      {/* Thinking indicator */}
      {bearState === 'thinking' && (
        <div className="sentinel-bear__thinking">
          <span>מעבד...</span>
        </div>
      )}

      {/* Success indicator */}
      {bearState === 'success' && (
        <div className="sentinel-bear__success">
          <span>✓</span>
        </div>
      )}
    </animated.div>
  );
};

export default InteractiveSentinelBear;
